<template>
  <q-card-section class="row justify-between">
    <h6 class="text-primary">Acoustic Properties</h6>
    <div class="q-gutter-md">
      <reference-popup-proxy
        icon="warning"
        color="red"
        tooltip="Disclaimer"
        size="md"
        text="The assumption is made that the pressure is sufficiently low for nonlinearity to be negligible. Please see ITRUSST Standardised Reporting paper for information about situations in which nonlinearity occurs."
      />
    </div>
  </q-card-section>
  <q-card-section class="q-gutter-xs">
    <q-toolbar>
      <q-toolbar-title>
        <h6 class="text-primary">Free-Field</h6>
      </q-toolbar-title>
    </q-toolbar>
    <q-btn-toggle
      no-caps
      spread
      v-model="selectedFreeFieldWaterProperties"
      toggle-color="primary"
      :color="isDarkModeGlobal ? '' : 'white'"
      text-color="primary"
      dense
      :options="freefieldWaterPropertyOptions"
    />
    <q-input
      :dark="isDarkModeGlobal"
      label="Density [kg/m3]"
      filled
      dense
      :disable="selectedFreeFieldWaterProperties !== 'Custom'"
      type="number"
      step="0.000001"
      v-model="rho_kg_m3_FF"
      :rules="rules"
      hide-bottom-space

      @update:modelValue="executeCalculation(rho_kg_m3_FF, 'pr')"
    >
      <template v-if="selectedFreeFieldWaterProperties !== 'Custom'" #after>
        <reference-popup-proxy
          :link="'https://doi.org/10.13099/VIP21000-04-1'"
        />
      </template>
    </q-input>
    <q-input
      :dark="isDarkModeGlobal"
      label="Speed Of Sound [m/s]"
      filled
      dense
      :disable="selectedFreeFieldWaterProperties !== 'Custom'"
      type="number"
      step="0.000001"
      v-model="c_m_s_FF"
      :rules="rules"

      @update:modelValue="executeCalculation(c_m_s_FF, 'pr')"
      hide-bottom-space
    >
      <template v-if="selectedFreeFieldWaterProperties !== 'Custom'" #after>
        <reference-popup-proxy
          :link="'https://doi.org/10.13099/VIP21000-04-1'"
        />
      </template>
    </q-input>
    <q-input
      :dark="isDarkModeGlobal"
      :model-value="calculatorStore.z_Rayl_FF.toFixed(2)"
      readonly
      outlined
      label="Acoustic Impedance [kg/(mm2*s)]"
      dense
      type="number"
    />
    </q-card-section>
  <q-card-section class="q-gutter-xs">
    <q-toolbar>
      <q-toolbar-title>
        <h6 class="text-primary">In Situ</h6>
      </q-toolbar-title>
    </q-toolbar>

    <q-btn-toggle
      no-caps
      spread
      v-model="selectedInSituWaterProperties"
      toggle-color="primary"
      :color="isDarkModeGlobal ? '' : 'white'"
      text-color="primary"
      dense
      :options="inSituWaterPropertyOptions"
    />
    <q-input
      :dark="isDarkModeGlobal"
      label="Density [kg/m3]"
      filled
      dense
      :disable="selectedInSituWaterProperties !== 'Custom'"
      type="number"
      step="0.000001"
      v-model="rho_kg_m3_IS"
      :rules="rules"
      hide-bottom-space

      @update:modelValue="executeCalculation(rho_kg_m3_IS, 'pr')"
    >
      <template v-if="selectedInSituWaterProperties !== 'Custom'"
                #after
      >
        <reference-popup-proxy
          :link="'https://doi.org/10.13099/VIP21000-04-1'"
        />
      </template>
    </q-input>
    <q-input
      :dark="isDarkModeGlobal"
      label="Speed Of Sound [m/s]"
      filled
      dense
      :disable="selectedInSituWaterProperties !== 'Custom'"
      type="number"
      step="0.000001"
      v-model="c_m_s_IS"
      :rules="rules"

      @update:modelValue="executeCalculation(c_m_s_IS, 'pr')"
      hide-bottom-space
    >
      <template v-if="selectedInSituWaterProperties !== 'Custom'" #after>
        <reference-popup-proxy
          :link="'https://doi.org/10.13099/VIP21000-04-1'"
        />
      </template>
    </q-input>
    <q-input
      :dark="isDarkModeGlobal"
      :model-value="calculatorStore.z_Rayl_IS.toFixed(2)"
      readonly
      outlined
      label="Acoustic Impedance [kg/(mm2*s)]"
      dense
      type="number"
    />
    </q-card-section>
</template>
<script setup lang="ts">
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {computed, ref, watch} from "vue"
import ReferencePopupProxy from "./PopupProxyLiteratureReference.vue"

const calculatorStore = useCalculatorStore()
const {
  focal_depth_mm,
  thickness_of_skull_mm,
  thickness_of_scalp_mm,
  coupling_thickness_mm,
  rho_kg_m3_FF,
  c_m_s_FF,
  rho_kg_m3_IS,
  c_m_s_IS,
  isDarkModeGlobal,
  selectedFreeFieldWaterProperties,
  selectedInSituWaterProperties,
  loadingValue
} = storeToRefs(calculatorStore)

const material = {
  Brain: {
    density: 1046,
    speed: 1546.3
  },
  Water: {
    density: 998,
    speed: 1481
  }
}

const freefieldWaterPropertyOptions = [
  { label: 'Water', value: 'Water' },
  { label: 'Custom', value: 'Custom' }
]

const inSituWaterPropertyOptions = [
  { label: 'Brain', value: 'Brain' },
  { label: 'Custom', value: 'Custom' }
]

watch(selectedFreeFieldWaterProperties, (val) => {
  if (['Water'].includes(val)) {
    rho_kg_m3_FF.value = material[val].density
    c_m_s_FF.value = material[val].speed
    executeCalculation(rho_kg_m3_FF.value, 'pr')
  }
})

watch(selectedInSituWaterProperties, (val) => {
  if (['Brain'].includes(val)) {
    rho_kg_m3_IS.value = material[val].density
    c_m_s_IS.value = material[val].speed
    executeCalculation(rho_kg_m3_IS.value, 'pr')
  }
})

const rules = [val => !!val || 'This field is required', val => val > 0 || 'Value must be > 0']
const minimumValueFocalDepth = computed(() => +coupling_thickness_mm.value + +thickness_of_scalp_mm.value + +thickness_of_skull_mm.value)

const blurActiveInput = () => {
  const activeElement = document.activeElement;
  if (activeElement && activeElement.tagName === 'INPUT') {
    activeElement.blur()
  }
};

let debounceTimeout = null // Timeout reference for debounce
let interval = null // Timeout reference for interval
const loading = ref(false) // Tracks spinner visibility

let totalTime = 3000
let intervalTime = 100 // Time between each count
function executeCalculation(val: number, calcType: string) {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
    clearInterval(interval)
    loadingValue.value = 0
  }

  loading.value = true;
  interval = setInterval(() => {
  loadingValue.value += 0.1;
    if (loadingValue.value > 3) {
      clearInterval(interval); // Stop the interval when count reaches 100
      loadingValue.value = 0
    }
  }, intervalTime);
      // Start debounce timer
  debounceTimeout = setTimeout(() => {
        // After debounce delay
    loading.value = false;
    if (val > 0 && focal_depth_mm.value >= minimumValueFocalDepth.value) {
      switch (calcType) {
        case 'acoustic_power':
          calculatorStore.updateByAcousticPower()
          break
        case 'transducer_efficiency':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'electrical_power':
          calculatorStore.updateByElectricalPowerOrTransducerEfficiency()
          break
        case 'pr':
          calculatorStore.updateByPr()
          break
        case 'MI':
          calculatorStore.updateByMI()
          break
        case 'non_derated_MI':
          calculatorStore.updateByNonDeratedMI()
          break
        default:
          calculatorStore.updateByIsppa()
          break
      }
      calculatorStore.clearNotifications()

      blurActiveInput()
    } else {
      console.error("empty value or 0 not allowed")
    }
  }, totalTime);
}
</script>
